<template>
    <div class="lanoga-select" @click="open" v-click-outside="close" :class="{'read-only': readonly}">
        <div class="input-wrapper d-flex">
            <input type="text" class="requiredInput" :value="selectedItem" :name="name" v-if="required" required>
            <div  class="select-text flex-fill" v-html="displayValue" v-b-popover.hover.top="showPopOver ? displayValue:null"></div>
            <div class="select-clear-wrapper">
                <button @click="open" type="button" v-if="!selectedItem">
                    <i class="fas fa-chevron-down"></i>
                </button>
                <button @click="clear" type="button" v-if="selectedItem">
                    <i class="fas fa-times"></i>
                </button>
            </div>
        </div>
        <div class="select-option-list" v-show="isOpenList">
            <input class="select-search" type="text" v-model="queryText" ref="search"
                   :placeholder="options.locale && options.locale['searchPlaceholder'] ? options.locale['searchPlaceholder'] : ''">
            <div v-if="loading" class="lds-ellipsis">
                <div></div>
                <div></div>
                <div></div>
                <div></div>
            </div>
            <div class="text-center" v-if="filteredOptionList.length < 1 && !loading">
                {{options.locale && options.locale['noResults'] ? options.locale['noResults'] : ''}}
            </div>
            <ul v-if="filteredOptionList.length > 0 && !loading">
                <li v-for="(item, index) in filteredOptionList" v-bind:key="index" @click="selectItem(item, $event)"
                        v-html="resolvedOptions.formatOption(item)">
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
import ClickOutside from 'vue-click-outside'

export default {
    name: 'LanogaSelect',

    directives: {
        ClickOutside
    },

    props: {
        value: { type: [Object, String, Number], default: null },
        options: { type: Object, required: true },


        additionalApiCallbackParams: {
            type: Object,
            default: () => ({})
        },

        externalOptionList: { type: [Array,Object], default: null },
        readonly: { type: Boolean, default: false },
        required: { type: Boolean, default: false },
        name: { type: String },
        showPopOver: { type: Boolean, default: false }
    },

    data() {
        return {
            optionList: [],
            filteredOptionList: [],
            selectedItem: null,
            queryText: '',
            isOpenList: false,
            loading: false
        }
    },

    computed: {

        resolvedOptions() {
            return {
                apiCallback: null,
                formatText: v => v,
                formatOption: v => v,
                filteredAttr: null,
                minimumInputLength: 0,
                searchOnServer: false,
                locale: {},
                returnValue: null,
                additionalApiCallbackParams: {}, 
                ...this.options
            }
        },


        effectiveApiParams() {
            return Object.keys(this.additionalApiCallbackParams || {}).length
                ? this.additionalApiCallbackParams
                : this.resolvedOptions.additionalApiCallbackParams || {}
        },

        displayValue() {
            if (!this.selectedItem) return ''
            return this.resolvedOptions.formatText(this.selectedItem)
        }
    },

    created() {
        this.initOptions()
    },

    watch: {

        value: {
            immediate: true,
            handler() {
                this.syncSelectedItem()
            }
        },

        queryText() {
            this.filterOptions()
        },

        externalOptionList: {
            immediate: true,
            deep: true,
            handler(list) {
                if (!Array.isArray(list)) {
                    this.optionList = []
                    this.filteredOptionList = []
                    return
                }

                this.optionList = list
                this.filterOptions()
                this.syncSelectedItem()
            }
        },

        effectiveApiParams: {
            deep: true,
            handler() {
                if (this.resolvedOptions.apiCallback) {
                    this.fetchInitialOptions()
                }
            }
        },

        'resolvedOptions.apiCallback'() {
            this.initOptions()
        }
    },

    methods: {

        initOptions() {
            if (this.externalOptionList) return

            if (!this.resolvedOptions.apiCallback) {
                this.optionList = []
                return
            }

            if (!this.resolvedOptions.searchOnServer) {
                this.fetchInitialOptions()
            }
        },

        fetchInitialOptions() {
            this.loading = true

            this.resolvedOptions.apiCallback({
                params: this.effectiveApiParams
            })
                .then(res => {
                    this.optionList = res.data || []
                    this.filterOptions()
                    this.syncSelectedItem()
                })
                .finally(() => {
                    this.loading = false
                })
        },


        filterOptions() {
            if (this.resolvedOptions.searchOnServer) {
                if (this.queryText.length >= this.resolvedOptions.minimumInputLength) {
                    this.searchOnServer()
                } else {
                    this.filteredOptionList = []
                }
                return
            }

            if (!this.queryText) {
                this.filteredOptionList = [...this.optionList]
                return
            }

            const attr = this.resolvedOptions.filteredAttr
            this.filteredOptionList = this.optionList.filter(item =>
                attr && item[attr]
                    ? String(item[attr]).toLowerCase().includes(this.queryText.toLowerCase())
                    : true
            )
        },

        searchOnServer() {
            this.loading = true

            this.resolvedOptions.apiCallback({
                params: {
                    ...this.effectiveApiParams,
                    term: this.queryText
                }
            })
                .then(res => {
                    this.filteredOptionList = res.data || []
                })
                .finally(() => {
                    this.loading = false
                })
        },

        syncSelectedItem() {
            if (!this.value) {
                this.selectedItem = null
                return
            }

            if (this.resolvedOptions.returnValue) {
                this.selectedItem =
                    this.optionList.find(
                        v => v[this.resolvedOptions.returnValue] == this.value
                    ) || null
            } else {
                this.selectedItem = this.value
            }
        },

        selectItem(item, event) {
            event.preventDefault()
            event.stopPropagation()

            const output = this.resolvedOptions.returnValue
                ? item[this.resolvedOptions.returnValue]
                : item

            this.selectedItem = item
            this.$emit('input', output)
            this.$emit('select', item)
            this.close()
        },

        clear(event) {
            event?.preventDefault()
            event?.stopPropagation()
            if (this.readonly) return

            this.selectedItem = null
            this.$emit('input', null)
            this.$emit('cleared')
        },

        open() {
            if (this.readonly) return
            this.isOpenList = true
            this.$nextTick(() => this.$refs.search?.focus())
        },

        close() {
            this.isOpenList = false
            this.queryText = ''
        }
    }
}
</script>



<style scoped>
    .lanoga-select {
        background-color: white;
        position: relative;
        height: 38px !important;
        border: 1px solid #ced4da;
        padding: 0 0.5rem;
        font-size: 0.875rem;
        line-height: 1.5;
        border-radius: 0.2rem;
    }

    .lanoga-select * {
        cursor: pointer;
    }

    .lanoga-select.read-only {
        background-color: #e9ecef;
    }

    .input-wrapper {
        height: 100%;
        max-width: 100%;
    }

    .requiredInput {
        position: absolute;
        z-index: -1000;
        width: 1px;
        height: 1px;
        border: none;
    }

    .select-text {
        color: #000000;
        max-width: 100%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .select-clear-wrapper button {
        background-color: transparent;
        color: #888888;
        border: none;
    }

    .select-option-list {
        margin-left: -0.5rem;
        background-color: white;
        position: absolute;
        width: 100%;
        border: 1px solid #ced4da;
        border-radius: 0.2rem;
        z-index: 3;
        max-height: 200px;
        overflow: auto;
    }

    .select-search {
        border: 1px solid #ced4da;
        border-radius: 0.2rem;
        width: 100%;
        padding: 4px !important;
        outline-offset: -2px;
        -webkit-appearance: none;
    }

    .select-option-list ul {
        list-style: none;
        margin: 0;
        padding: 0;
    }

    .select-option-list ul li {
        padding: 6px;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
        -webkit-user-select: none;
        cursor: pointer;
    }

    .select-option-list ul li:hover {
        background-color: var(--default-color);
        color: white;
    }

    /*Loader*/

    .lds-ellipsis {
        display: block;
        position: relative;
        width: 64px;
        height: 35px;
        margin: auto;
    }

    .lds-ellipsis div {
        position: absolute;
        top: 12px;
        width: 11px;
        height: 11px;
        border-radius: 50%;
        background: var(--default-color);
        animation-timing-function: cubic-bezier(0, 1, 1, 0);
    }

    .lds-ellipsis div:nth-child(1) {
        left: 6px;
        animation: lds-ellipsis1 0.6s infinite;
    }

    .lds-ellipsis div:nth-child(2) {
        left: 6px;
        animation: lds-ellipsis2 0.6s infinite;
    }

    .lds-ellipsis div:nth-child(3) {
        left: 26px;
        animation: lds-ellipsis2 0.6s infinite;
    }

    .lds-ellipsis div:nth-child(4) {
        left: 45px;
        animation: lds-ellipsis3 0.6s infinite;
    }

    @keyframes lds-ellipsis1 {
        0% {
            transform: scale(0);
        }
        100% {
            transform: scale(1);
        }
    }

    @keyframes lds-ellipsis3 {
        0% {
            transform: scale(1);
        }
        100% {
            transform: scale(0);
        }
    }

    @keyframes lds-ellipsis2 {
        0% {
            transform: translate(0, 0);
        }
        100% {
            transform: translate(19px, 0);
        }
    }
</style>
